import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import { isPresent } from '@ember/utils'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import { studentStatsClassQueryFilter } from 'teacher-ui-client/subscription-type/book-library/shares/index/route'
import type BookLibraryStudentStat from 'teacher-ui-client/models/book-library/student-stat'
import type SessionService from 'teacher-ui-client/services/session'
import type { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import doAction from 'teacher-ui-client/modifiers/do-action'
import perform from 'ember-concurrency/helpers/perform'
import { hash } from '@ember/helper'
import { t } from 'ember-intl'
import { array } from '@ember/helper'
import { LinkTo } from '@ember/routing'
import { and, eq } from 'ember-truth-helpers'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import type StudentStat from 'teacher-ui-client/models/book-library/student-stat'
import { isNumber } from 'lodash'
import type { PhosphorIconName } from 'phosphor-icons'

interface Query {
  page: {
    number: number
    size: number
  }
  sort: string
  filter: {
    has_classes?: boolean | undefined
    teacher_ids?: string[] | undefined
    student_ids?: string[] | undefined
    school_id: string
    name?: string
  }
}

interface Args {
  expandedStudentId?: string
}

interface Signature {
  Element: HTMLTableElement
  Args: Args
  Blocks: {
    expandedRow: []
  }
}

function getSortIcon(
  { sortable, dataKey }: { sortable?: boolean; dataKey?: string },
  sortBy: string,
): PhosphorIconName | undefined {
  if (!sortable) return
  switch (sortBy) {
    case `${dataKey}`:
      return 'phosphor-caret-up-fill'
    case `-${dataKey}`:
      return 'phosphor-caret-down-fill'
    default:
      return 'phosphor-caret-up-down-fill'
  }
}

function calculateSortBy({ sortable, dataKey }: { sortable?: boolean; dataKey?: string }, sortBy: string) {
  if (!sortable) return
  switch (sortBy) {
    case `${dataKey}`:
      return `-${dataKey}`
    case `-${dataKey}`:
      return `${dataKey}`
    default:
      return `${dataKey}`
  }
}

export class BookLibraryStudentsTable extends Component<Signature> {
  @service store!: Store

  @service session!: SessionService

  @service gradeSets!: GradeSetsService

  @queryParam('subscription-type/book-library/shares/index/by-student') page!: number

  @queryParam('subscription-type/book-library/shares/index/by-student') size!: number

  @queryParam('subscription-type/book-library/shares/index/by-student') sort!: string

  @queryParam('subscription-type/book-library/shares/index') schoolClassId!: string | SchoolClassOptionTypes

  @queryParam('subscription-type/book-library/shares/index') search!: string

  get query() {
    const query: Query = {
      page: {
        number: this.page,
        size: this.size,
      },
      sort: this.sort,
      filter: {
        school_id: this.session.currentSchool.id,
        ...studentStatsClassQueryFilter(this.schoolClassId, this.store, this.session),
      },
    }
    if (isPresent(this.search)) {
      query.filter.name = this.search
    }
    return query
  }

  queryStudents = task(
    { restartable: true },
    waitFor(async (query: Query) => {
      await timeout(100)
      return this.store.query<StudentStat>('book-library/student-stat', { ...query })
    }),
  )

  get columns() {
    return [
      { classNames: 'w-12' }, // toggle edit (no label)
      {
        labelKey: 'bookLibrary.components.studentsTable.headers.firstName',
        dataKey: 'first_name',
        sortable: true,
        classNames: 'w-3/12',
      },
      {
        labelKey: 'bookLibrary.components.studentsTable.headers.lastName',
        dataKey: 'last_name',
        sortable: true,
        classNames: 'w-3/12',
      },
      {
        labelKey: 'bookLibrary.components.studentsTable.headers.schoolClasses',
        sortable: false,
        classNames: 'w-5/12',
      },
      {
        labelKey: 'bookLibrary.components.studentsTable.headers.grade',
        dataKey: 'grade_position',
        sortable: true,
        classNames: 'w-1/12',
      },
      {
        labelKey: 'bookLibrary.components.studentsTable.headers.currentCollectionCount',
        dataKey: 'active_and_pending_shares_collection_count',
        sortable: true,
        classNames: 'w-1/12',
      },
      {},
    ].map((column) => ({
      sortIcon: getSortIcon(column, this.sort),
      sortBy: calculateSortBy(column, this.sort),
      ...column,
    }))
  }

  /**
   * TODO: lastSuccessful is set both when the task is performed, and when that perform finishes..
   * That results in the rows getter to update twice, causing 2 table renders
   * https://github.com/machty/ember-concurrency/issues/467
   */
  get studentStats(): BookLibraryStudentStat[] {
    return this.queryStudents.lastSuccessful?.value ?? []
  }

  get rows() {
    return this.studentStats.map((studentStat) => ({
      id: studentStat.id,
      firstName: studentStat.firstName,
      lastName: studentStat.lastName,
      schoolClassesLabel: studentStat.schoolClassNames,
      gradeLabel: this.gradeSets.nameFromGradeSetByPosition(studentStat.gradePosition),
      currentSharesCollectionCount: studentStat.activeSharesCollectionCount + studentStat.pendingSharesCollectionCount,
    }))
  }

  <template>
    <table class="divide-dusty-black-100 divide-y" {{doAction (perform this.queryStudents this.query)}} ...attributes>
      <thead>
        <tr>
          {{#each this.columns as |col|}}
            {{#if col.sortable}}
              <th data-test-col={{col.dataKey}} class="{{col.classNames}} px-4 py-2 font-normal">
                {{! Ideally would be a QP-only linkto, but we need to specify the route because the user could be on the
            show or edit routes, which require a an ID dynamic segment. }}
                <LinkTo
                  @route="subscription-type.book-library.shares.index.by-student"
                  @query={{hash sort=col.sortBy}}
                  class="text-dusty-black-250 flex items-center space-x-2"
                >
                  <span>{{t col.labelKey}}</span>
                  {{#if col.sortIcon}}
                    <PhosphorIcon @icon={{col.sortIcon}} />
                  {{/if}}
                </LinkTo>
              </th>
            {{else}}
              <th class="{{col.classNames}} text-dusty-black-250 px-4 py-2 font-normal">
                {{if col.labelKey (t col.labelKey)}}
              </th>
            {{/if}}
          {{/each}}
        </tr>
      </thead>
      <tbody class="divide-dusty-black-100 divide-y {{if this.queryStudents.isRunning 'opacity-50'}}">
        {{#each this.rows as |row|}}
          {{#let (eq @expandedStudentId row.id) as |isExpanded|}}
            <tr data-test-student-row-id={{row.id}}>
              <td class="pl-6">
                {{#if isExpanded}}
                  <LinkTo @route="subscription-type.book-library.shares.index.by-student">
                    <PhosphorIcon @icon="phosphor-caret-right-bold" class="text-dusty-black-250 h-8 w-8 rotate-90" />
                  </LinkTo>
                {{else}}
                  <LinkTo @route="subscription-type.book-library.shares.index.by-student.show" @models={{array row.id}}>
                    <PhosphorIcon @icon="phosphor-caret-right-bold" class="text-dusty-black-250 h-8 w-8" />
                  </LinkTo>
                {{/if}}
              </td>
              <td class="px-4 py-2" data-test-cell="first-name">
                {{row.firstName}}
              </td>
              <td class="px-4 py-2" data-test-cell="last-name">
                {{row.lastName}}
              </td>
              <td class="px-4 py-2" data-test-cell="school-classes">
                {{row.schoolClassesLabel}}
              </td>
              <td class="px-4 py-2" data-test-cell="grade">
                {{row.gradeLabel}}
              </td>
              <td class="px-4 py-2" data-test-cell="currentSharesCollectionCount">
                {{row.currentSharesCollectionCount}}
              </td>
            </tr>
            {{#if (and isExpanded (has-block "expandedRow"))}}
              <tr data-test-student-expanded-row-id={{row.id}}>
                <td colspan={{this.columns.length}} class="py-4 pr-4 pl-16">
                  {{yield to="expandedRow"}}
                </td>
              </tr>
            {{/if}}
          {{/let}}
        {{else}}
          <tr>
            <td colspan={{this.columns.length}} class="py-12 text-center">
              {{#if this.queryStudents.lastSuccessful}}
                <NoResultsMessage />
              {{else}}
                <FidgetLoadingIndicator
                  data-test-students-table-loading
                  @show={{true}}
                  @centered={{true}}
                  @appearance="small"
                  @overlay={{false}}
                />
              {{/if}}
            </td>
          </tr>
        {{/each}}
      </tbody>
      {{#if (isNumber this.queryStudents.lastSuccessful.value.meta.total-pages)}}
        {{#if (isNumber this.queryStudents.lastSuccessful.value.meta.total)}}
          <tfoot>
            <tr>
              <td colspan={{this.columns.length}}>
                <BasePageSelector
                  class="my-4"
                  @pageCount={{this.queryStudents.lastSuccessful.value.meta.total-pages}}
                  @pageNumberKey="page"
                  @itemCount={{this.queryStudents.lastSuccessful.value.meta.total}}
                />
              </td>
            </tr>
          </tfoot>
        {{/if}}
      {{/if}}
    </table>
  </template>
}

export default BookLibraryStudentsTable
